import * as React from "react";

const BOOKINGS_KEY = "spayzo.bookings";
const REVIEWS_KEY = "spayzo.reviews";

export type UserReview = {
  id: string;
  spaceId: string;
  name: string;
  initials: string;
  rating: number;
  date: string;
  text: string;
  createdAt: number;
};

function readJSON<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJSON(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(new Event(`${key}:changed`));
  } catch {
    /* ignore */
  }
}

export function markBooked(spaceId: string) {
  const list = readJSON<string[]>(BOOKINGS_KEY, []);
  if (!list.includes(spaceId)) {
    list.push(spaceId);
    writeJSON(BOOKINGS_KEY, list);
  }
}

export function hasBookedSpace(spaceId: string): boolean {
  return readJSON<string[]>(BOOKINGS_KEY, []).includes(spaceId);
}

export function getReviews(spaceId: string): UserReview[] {
  return readJSON<UserReview[]>(REVIEWS_KEY, []).filter((r) => r.spaceId === spaceId);
}

export function addReview(
  input: Omit<UserReview, "id" | "createdAt" | "initials" | "date">,
): UserReview {
  const all = readJSON<UserReview[]>(REVIEWS_KEY, []);
  const initials =
    input.name
      .trim()
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";
  const date = new Date().toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
  const review: UserReview = {
    ...input,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    initials,
    date,
    createdAt: Date.now(),
  };
  all.push(review);
  writeJSON(REVIEWS_KEY, all);
  return review;
}

export function useHasBooked(spaceId: string) {
  const [booked, setBooked] = React.useState(false);
  React.useEffect(() => {
    const update = () => setBooked(hasBookedSpace(spaceId));
    update();
    const handler = () => update();
    window.addEventListener(`${BOOKINGS_KEY}:changed`, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(`${BOOKINGS_KEY}:changed`, handler);
      window.removeEventListener("storage", handler);
    };
  }, [spaceId]);
  return booked;
}

export function useSpaceReviews(spaceId: string) {
  const [reviews, setReviews] = React.useState<UserReview[]>([]);
  React.useEffect(() => {
    const update = () => setReviews(getReviews(spaceId));
    update();
    const handler = () => update();
    window.addEventListener(`${REVIEWS_KEY}:changed`, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(`${REVIEWS_KEY}:changed`, handler);
      window.removeEventListener("storage", handler);
    };
  }, [spaceId]);
  return reviews;
}
